import React, { Fragment } from 'react';
import { styled } from 'dripsy';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';
import { runInAction } from 'mobx';

import {
  AuthFormLayout,
  AuthLayout,
  Form,
  Field,
  Button,
  ButtonStyleEnum,
  BaseText,
  Separator,
} from '@components';
import {
  forgotPasswordSchema,
  ForgotPasswordSchemaDataType,
} from '@validation';
import {
  AuthNavigatorNavigationPropType,
  screens,
} from '@navigation';
import { IS_MOBILE } from '@utils';
import { useAuthModel } from '@models/AuthModel';
import { useAuthErrorHandler } from '../hooks/useAuthErrorHandler';
import { SubmitButton } from './components/SubmitButton';
import { useNavOptionsSet } from '@hooks';

const BottomTextSemi = styled(BaseText)({
  variant: 'text.small',
  fontWeight: 'semi',
  color: '$text400',
});

type NavigationPropType =
  AuthNavigatorNavigationPropType<'ForgotPassword'>;

export interface IForgotPasswordScreenStore {
  sentToEmail: string | null;
  countdownTimeLeft: number;
  isActiveCountdown: boolean;
  countdownIntervalId: ReturnType<typeof setInterval> | null;

  markAsSent(email: string): void;
  get isSent(): boolean;
  get formattedSentToEmail(): string | undefined;
  get countdownTimeLeftInSeconds(): number;
  clearCountdown(): void;
  runCountdown(): void;
  decreaseTime(): void;
}

export const ForgotPasswordScreen = observer(() => {
  const { t } = useTranslation();
  const { handleError } = useAuthErrorHandler();
  const nav = useNavigation<NavigationPropType>();
  const authModel = useAuthModel();
  const store = useLocalObservable<IForgotPasswordScreenStore>(
    () => ({
      sentToEmail: null as string | null,
      countdownTimeLeft: 60000,
      isActiveCountdown: false,
      countdownIntervalId: null,

      markAsSent(email: string) {
        this.sentToEmail = email;

        this.runCountdown();
      },

      get isSent() {
        return !!this.sentToEmail;
      },

      get formattedSentToEmail() {
        if (!this.sentToEmail) {
          return;
        }

        // example@email.com -> e***@email.com
        const [prefix, domain] = this.sentToEmail.split('@');

        return `${prefix[0]}***@${domain}`;
      },

      get countdownTimeLeftInSeconds() {
        return this.countdownTimeLeft / 1000;
      },

      clearCountdown() {
        this.isActiveCountdown = false;
        this.countdownTimeLeft = 60000;

        if (this.countdownIntervalId) {
          clearInterval(this.countdownIntervalId);
          this.countdownIntervalId = null;
        }
      },

      decreaseTime() {
        this.countdownTimeLeft = this.countdownTimeLeft - 1000;

        if (this.countdownTimeLeft <= 0) {
          this.isActiveCountdown = false;
        }
      },

      runCountdown() {
        this.clearCountdown();
        this.isActiveCountdown = true;
        this.countdownIntervalId = setInterval(() => {
          runInAction(() => {
            this.decreaseTime();
          });
        }, 1000);
      },
    }),
  );

  useNavOptionsSet({ title: t('forgotPassword.title') });

  async function forgotPassword(
    values: ForgotPasswordSchemaDataType,
  ) {
    try {
      await authModel.forgotPassword.run(values);

      store.markAsSent(values.email);
    } catch (error) {
      handleError({
        error,
        email: values.email,
      });
    }
  }

  function navigateToSignIn() {
    nav.navigate(screens.SignIn);
  }

  const initialValues: ForgotPasswordSchemaDataType = {
    email: '',
  };

  const title = store.isSent
    ? t('forgotPassword.titleSent')
    : t('forgotPassword.title');

  return (
    <AuthLayout
      onPressHeaderButton={navigateToSignIn}
      headerButtonText={t('forgotPassword.signIn')}
    >
      <AuthFormLayout
        title={title}
        bottomText={
          !store.isSent ? (
            t('forgotPassword.text')
          ) : (
            <Trans
              i18nKey="forgotPassword.weSentEmail"
              values={{ email: store.formattedSentToEmail }}
              components={{
                bold: <BottomTextSemi />,
              }}
            />
          )
        }
      >
        <Form<ForgotPasswordSchemaDataType>
          onSubmit={forgotPassword}
          initialValues={initialValues}
          validationSchema={forgotPasswordSchema}
          validateOnMount
        >
          <Field
            name="email"
            placeholder={t('forgotPassword.email')}
            autoCapitalize="none"
            keyboardType="email-address"
          />
          <Separator />
          <SubmitButton store={store} />
          {IS_MOBILE && (
            <Fragment>
              <Separator size="$5" />
              <Button
                iconLeft="small-arrow-left-light"
                onPress={navigateToSignIn}
                buttonStyle={ButtonStyleEnum.Outline}
              >
                {t('forgotPassword.goBackToSingIn')}
              </Button>
            </Fragment>
          )}
        </Form>
      </AuthFormLayout>
    </AuthLayout>
  );
});
